import { useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import Button from 'components/CncUI/Button';
import Typography from 'components/CncUI/Typography';
import Accordion from 'components/CncUI/Accordion';
import Loader from 'components/Loader';
import FileViewer from 'components/FileViewer';
import FileDownloader from 'components/FileDownloader';
import AddNewModal from './modals/AddNew';
import EmptyFolder from './EmptyFolder';
import List from './List';
import ProvinceList from './ProvinceList';

import useCreateW4 from './hooks/useCreateW4';
import useW4Locks from './hooks/useW4Locks';
import useStates from './hooks/useStates';
import useError from './hooks/useError';
import useW4s from './hooks/useW4s';
import useGetW4Url from './hooks/useGetW4Url';

import useCountry from 'hooks/useCountry';
import useQuery from 'hooks/useQuery';
import base64ToArrayBuffer from 'utilities/base64ToArrayBuffer';

const useStyles = makeStyles(({ breakpoints }) => ({
  header: {
    marginTop: 40,
    marginBottom: 0,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  lists: {
    marginTop: 16,
  },
  stateContainer: {
    padding: 16,
  },
}));

const Home = props => {
  const { location, history, prefix } = props;
  const { country } = useCountry();
  const classes = useStyles();
  const [createW4] = useCreateW4();
  const getW4Url = useGetW4Url();
  const getW4File = useQuery('getW4File', {
    request: id => ({ id }),
    response: res => {
      const data = res.data.getW4File || '';
      if (data) {
        const arraybuffer = base64ToArrayBuffer(data);
        const fileBlob = new Blob([arraybuffer], { type: 'application/pdf' });
        return URL.createObjectURL(fileBlob);
      }
      return '';
    },
  });
  const onError = useError(
    `There was an error displaying your W-4s. Please try again later or
    contact product support if the issue persists.`,
  );
  const { loading, data: w4Locks } = useW4Locks();
  const { loading: w4sLoading, data: w4s } = useW4s({
    variables: { country },
    onError,
  });
  const { data: states } = useStates({ onError, variables: { country } });
  const [addNew, setAddNew] = useState(false);
  const [downloader, setDownloader] = useState('');
  const [viewer, setViewer] = useState('');
  const isCAN = country === 'CAN';

  // According to this https://stackoverflow.com/questions/57101831/react-router-how-do-i-update-the-url-without-causing-a-navigation-reload
  // We are going to use window.history.replaceState to handle url difference
  // This is because the 2 routes uses the same page component and we don't
  // want to reload the same component if we are in the wrong country.
  if (country === 'CAN' && location.pathname !== `${prefix}/td1s`) {
    history.replace(`${prefix}/td1s`);
  } else if (country === 'US' && location.pathname !== `${prefix}/w4s`) {
    history.replace(`${prefix}/w4s`);
  }

  const createNew = (stateId, country) =>
    createW4(stateId, country).then(({ id }) => {
      const base = country === 'CAN' ? 'td1s' : 'w4s';
      const parameter = country === 'CAN' ? 'province' : 'state';
      const type = !!stateId ? parameter : 'federal';
      const state = !!stateId ? `&state=${stateId}` : '';
      const url = `/${base}/new/${type}?id=${id}${state}`;
      history.push(url);
    });

  const federal = (w4s || []).filter(({ stateId }) => !stateId);
  const byStates = (states || [])
    .map(({ name, id }) => ({
      stateId: id,
      stateName: name,
      stateW4s: (w4s || []).filter(({ stateId }) => stateId === id),
    }))
    .filter(({ stateW4s }) => stateW4s.length > 0);

  const title = isCAN ? 'TD1' : 'W-4';

  return (
    <Grid id="W4s" container spacing={2}>
      <Grid item xs={12} id="Header">
        <Typography variant="h2" className={classes.header}>
          My {title}s
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.description} id="Description">
        <Typography variant="body2" size="long">
          Save time by keeping your {title}s current here. Note that it takes
          1-2 pay periods for newly filed {title}s to become active.
        </Typography>
      </Grid>
      {loading ? (
        <Loader />
      ) : (
        <Grid item xs={12}>
          <Button
            id="AddNewButton"
            startIcon={<AddIcon />}
            onClick={() => setAddNew(true)}
          >
            Add New {title}
          </Button>
          <AddNewModal
            federalLock={w4Locks && (w4Locks.payroll || w4Locks.caps)}
            open={addNew}
            onClose={() => setAddNew(false)}
            onConfirm={createNew}
          />
        </Grid>
      )}
      <Grid item xs={12} className={classes.lists}>
        {w4sLoading ? (
          <Loader />
        ) : isCAN ? (
          <ProvinceList
            items={w4s}
            states={states}
            setDownloader={setDownloader}
            setViewer={setViewer}
          />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h4">Federal {title}s</Typography>
            </Grid>
            <Grid item xs={12} id="FederalList">
              {federal.length > 0 ? (
                <List
                  items={federal}
                  setDownloader={setDownloader}
                  setViewer={setViewer}
                />
              ) : (
                <EmptyFolder type="Federal" country={country} />
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h4">
                {country === 'CAN' ? 'Provincial TD1s' : 'State W-4s'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {byStates.length > 0 ? (
                byStates.map(({ stateName, stateId, stateW4s }) => (
                  <Accordion key={stateId} id={`State${stateId}List`}>
                    <Accordion.Title>{stateName}</Accordion.Title>
                    <Accordion.Content className={classes.stateContainer}>
                      <List
                        items={stateW4s}
                        setDownloader={setDownloader}
                        setViewer={setViewer}
                      />
                    </Accordion.Content>
                  </Accordion>
                ))
              ) : (
                <EmptyFolder
                  type={country === 'CAN' ? 'Provincial' : 'State'}
                  country={country}
                />
              )}
            </Grid>
          </Grid>
        )}
        {downloader && (
          <FileDownloader
            urlFetcher={getW4Url}
            config={{ documentId: downloader }}
            onClose={() => setDownloader('')}
          />
        )}
        {viewer && (
          <FileViewer
            fileFetcher={getW4File}
            config={viewer}
            onClose={() => setViewer('')}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default Home;
